// A re-extract's line-set item (SAD-980) as what accepting it would change:
// one plain sentence, the money before → after, then each line added,
// removed or changed with its fields before → after. The diff is computed by
// pangaea when the item is staged; this only renders it.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ReviewReasonCode } from '@mono/proto-clients';
import { SpecraBadge, SpecraInlineBanner, SpecraKeyValueRow, SpecraSectionHeader, SpecraSurface, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { GlossaryText } from '../../../Glossary/GlossaryText';
export function isLineSetItem(review) {
    return review.reasonCode === ReviewReasonCode.AWARD_REREAD_LINE_SET && !!review.lineSetDiff;
}
const CHANGE_LABELS = {
    added: 'Added',
    removed: 'Removed',
    changed: 'Changed'
};
const CHANGE_BADGES = {
    added: 'success',
    removed: 'danger',
    changed: 'warning'
};
const SECTION_TITLES = {
    added: 'Lines the re-read adds',
    removed: 'Lines the re-read no longer has',
    changed: 'Lines the re-read changes'
};
const SECTION_HINTS = {
    added: 'Not on file today. Using the re-read lines adds them.',
    removed: 'On file today but not in the re-read. Using the re-read lines removes them.',
    changed: 'On file today and in the re-read, with different values. Only the differences are shown.'
};
function BeforeAfter({ change }) {
    const before = change.before || 'None';
    const after = change.after || 'None';
    if (!change.changed) {
        return /*#__PURE__*/ _jsx("span", {
            children: after
        });
    }
    return /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex flex-wrap items-baseline gap-[var(--space-1-5)]",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground line-through",
                "data-side": "before",
                children: before
            }),
            /*#__PURE__*/ _jsx("span", {
                "aria-hidden": "true",
                children: "→"
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "sr-only",
                children: "becomes"
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "font-medium text-foreground",
                "data-side": "after",
                children: after
            })
        ]
    });
}
function fieldValue(line, f) {
    if (line.change === 'added') return /*#__PURE__*/ _jsx("span", {
        children: f.after
    });
    if (line.change === 'removed') return /*#__PURE__*/ _jsx("span", {
        children: f.before
    });
    return /*#__PURE__*/ _jsx(BeforeAfter, {
        change: f
    });
}
function LineCard({ line }) {
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        radius: "md",
        className: "p-3",
        "data-testid": Testids.reviewItem.lineSetLine,
        "data-clin": line.clinNumber,
        "data-change": line.change,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: CHANGE_BADGES[line.change] ?? 'neutral',
                        children: CHANGE_LABELS[line.change] ?? line.change
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "span",
                        size: "body",
                        className: "font-medium",
                        children: /*#__PURE__*/ _jsx(GlossaryText, {
                            text: `CLIN ${line.clinNumber}`
                        })
                    }),
                    line.description && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "span",
                        size: "body",
                        tone: "muted",
                        children: line.description
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-2 space-y-1",
                children: line.fields.filter((f)=>f.field !== 'description' || line.change === 'changed').map((f)=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: /*#__PURE__*/ _jsx(GlossaryText, {
                            text: f.label
                        }),
                        value: fieldValue(line, f),
                        "data-testid": Testids.reviewItem.lineSetField,
                        "data-field": f.field
                    }, f.field))
            })
        ]
    });
}
export function LineSetDiffView({ review }) {
    const diff = review.lineSetDiff;
    if (!diff) return null;
    const sections = [
        'added',
        'removed',
        'changed'
    ].map((kind)=>({
            kind,
            lines: diff.lines.filter((l)=>l.change === kind)
        })).filter((s)=>s.lines.length > 0);
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-[var(--space-3-5)] space-y-[var(--space-3-5)]",
        "data-testid": Testids.reviewItem.lineSetDiff,
        children: [
            /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                variant: "framed",
                tone: "info",
                title: "What using the re-read lines would change",
                "data-testid": Testids.reviewItem.lineSetSummary,
                "data-added": diff.added,
                "data-removed": diff.removed,
                "data-changed": diff.changed,
                children: /*#__PURE__*/ _jsx(GlossaryText, {
                    text: diff.summary
                })
            }),
            /*#__PURE__*/ _jsxs("section", {
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        headingAs: "h4",
                        title: /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: "The contract's money as the lines on file give it today, and as the re-read lines would.",
                            children: /*#__PURE__*/ _jsx("span", {
                                children: "Money, now → with the re-read lines"
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-2 space-y-1",
                        children: diff.money.map((m)=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: /*#__PURE__*/ _jsx(GlossaryText, {
                                    text: m.label
                                }),
                                value: /*#__PURE__*/ _jsx(BeforeAfter, {
                                    change: m
                                }),
                                "data-testid": Testids.reviewItem.lineSetMoney,
                                "data-field": m.field,
                                "data-changed": m.changed ? 'true' : 'false'
                            }, m.field))
                    })
                ]
            }),
            sections.map((s)=>/*#__PURE__*/ _jsxs("section", {
                    "data-testid": Testids.reviewItem.lineSetSection,
                    "data-change": s.kind,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            variant: "group",
                            headingAs: "h4",
                            title: `${SECTION_TITLES[s.kind]} (${s.lines.length})`
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "p",
                            size: "meta",
                            tone: "muted",
                            className: "mt-1",
                            children: SECTION_HINTS[s.kind]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "mt-2 space-y-2",
                            children: s.lines.map((l)=>/*#__PURE__*/ _jsx(LineCard, {
                                    line: l
                                }, l.clinNumber))
                        })
                    ]
                }, s.kind)),
            diff.unchanged > 0 && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "meta",
                tone: "muted",
                "data-testid": Testids.reviewItem.lineSetUnchanged,
                children: diff.unchanged === 1 ? '1 other line reads the same and stays as it is.' : `${diff.unchanged} other lines read the same and stay as they are.`
            })
        ]
    });
}
